import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'dealer_token';

export const dealerApi = axios.create({ baseURL: API });

dealerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const setDealerToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const getDealerToken = () => localStorage.getItem(TOKEN_KEY);
export const clearDealerToken = () => localStorage.removeItem(TOKEN_KEY);

export const dealerAuth = {
  register: (data) => dealerApi.post('/dealers/auth/register', data).then((r) => r.data),
  login: (data) => dealerApi.post('/dealers/auth/login', data).then((r) => r.data),
  me: () => dealerApi.get('/dealers/auth/me').then((r) => r.data),
  updateProfile: (data) => dealerApi.put('/dealers/auth/profile', data).then((r) => r.data),
};

// Marketplace-scope actor context (dealer vs telecaller). Cheaper than /auth/me
// and returns which login is currently active. Used by DealerLayout to hide
// nav items telecallers should not see.
export const marketplaceActor = {
  me: () => dealerApi.get('/dealers/marketplace-leads/me').then((r) => r.data),
};

// CRM workflow — call updates, timeline, My Day, Pipeline
export const leadCrm = {
  addActivity: (leadId, payload) => dealerApi.post(`/dealers/marketplace-leads/${leadId}/activities`, payload).then((r) => r.data),
  listActivities: (leadId) => dealerApi.get(`/dealers/marketplace-leads/${leadId}/activities`).then((r) => r.data),
  updateTaskStatus: (leadId, payload) => dealerApi.post(`/dealers/marketplace-leads/${leadId}/task-status`, payload).then((r) => r.data),
  myDay: () => dealerApi.get('/dealers/marketplace-leads/my-day').then((r) => r.data),
  pipeline: (telecallerId, dateRange) => {
    const params = {};
    if (telecallerId) params.telecaller_id = telecallerId;
    if (dateRange?.from) params.from_date = dateRange.from;
    if (dateRange?.to) params.to_date = dateRange.to;
    return dealerApi.get('/dealers/marketplace-leads/pipeline', { params }).then((r) => r.data);
  },
};

// Telecaller management (main-dealer only)
export const telecallers = {
  list: () => dealerApi.get('/dealers/telecallers').then((r) => r.data),
  create: (payload) => dealerApi.post('/dealers/telecallers', payload).then((r) => r.data),
  update: (id, payload) => dealerApi.patch(`/dealers/telecallers/${id}`, payload).then((r) => r.data),
  resetPassword: (id, password) => dealerApi.post(`/dealers/telecallers/${id}/reset-password`, { password }).then((r) => r.data),
  remove: (id) => dealerApi.delete(`/dealers/telecallers/${id}`).then((r) => r.data),
  performance: () => dealerApi.get('/dealers/telecallers/performance').then((r) => r.data),
};

// Billing / message-cap
export const dealerBilling = {
  usage: () => dealerApi.get('/dealers/billing/usage').then((r) => r.data),
  updateSettings: (payload) => dealerApi.patch('/dealers/billing/settings', payload).then((r) => r.data),
};

export const dealerListings = {
  uploadPhotos: (files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return dealerApi
      .post('/dealers/listings/upload-photos', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  // One photo per HTTP request — bulletproof against Cloudflare's 100s timeout.
  // Used by AddCar.jsx so a single slow file can't tank the whole batch.
  uploadSinglePhoto: (file, { onProgress } = {}) => {
    const fd = new FormData();
    fd.append('files', file);
    return dealerApi
      .post('/dealers/listings/upload-photos', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90_000,
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      })
      .then((r) => r.data);
  },
  createDraft: (data) => dealerApi.post('/dealers/listings/draft', data).then((r) => r.data),
  importFromUrl: (url) => dealerApi.post('/dealers/listings/import-from-url', { url }).then((r) => r.data),
  analyze: (id) => dealerApi.post(`/dealers/listings/${id}/analyze`).then((r) => r.data),
  fetchIntelligence: (id, force = false) =>
    dealerApi.post(`/dealers/listings/${id}/fetch-intelligence`, null, { params: force ? { force: 'true' } : {} }).then((r) => r.data),
  enhancePhoto: (id, photoUrl) => {
    const fd = new FormData();
    fd.append('photo_url', photoUrl);
    return dealerApi.post(`/dealers/listings/${id}/enhance-photo`, fd).then((r) => r.data);
  },
  approve: (id, updates) => dealerApi.put(`/dealers/listings/${id}/approve`, updates).then((r) => r.data),
  regenerateDescription: (id) => dealerApi.post(`/dealers/listings/${id}/regenerate-description`).then((r) => r.data),
  publish: (id) => dealerApi.post(`/dealers/listings/${id}/publish`).then((r) => r.data),
  list: (status) => dealerApi.get('/dealers/listings', { params: status ? { status } : {} }).then((r) => r.data),
  get: (id) => dealerApi.get(`/dealers/listings/${id}`).then((r) => r.data),
  remove: (id) => dealerApi.delete(`/dealers/listings/${id}`).then((r) => r.data),
  markSold: (id, customer) => dealerApi.post(`/dealers/listings/${id}/sold`, customer).then((r) => r.data),
};

export const dealerCustomers = {
  list: () => dealerApi.get('/dealers/customers').then((r) => r.data),
};

export const dealerLeads = {
  list: (action) => dealerApi.get('/dealers/leads', { params: action ? { action } : {} }).then((r) => r.data),
};

// AI Reels (Instagram/Facebook short-form video generated from a listing)
export const dealerReels = {
  templates: () => dealerApi.get('/dealers/reels/templates').then((r) => r.data),
  usage: () => dealerApi.get('/dealers/reels/usage').then((r) => r.data),
  create: (payload) => dealerApi.post('/dealers/reels', payload).then((r) => r.data),
  list: (status) => dealerApi.get('/dealers/reels', { params: status ? { status } : {} }).then((r) => r.data),
  get: (id) => dealerApi.get(`/dealers/reels/${id}`).then((r) => r.data),
  regenerateCaption: (id, tone) =>
    dealerApi.post(`/dealers/reels/${id}/regenerate-caption`, { tone: tone || null }).then((r) => r.data),
  remove: (id) => dealerApi.delete(`/dealers/reels/${id}`).then((r) => r.data),
  retry: (id) => dealerApi.post(`/dealers/reels/${id}/retry`).then((r) => r.data),
};

// Marketplace leads (CSV/Excel-uploaded leads from CarDekho/OLX/etc.)
export const marketplaceLeads = {
  upload: (file, { source_override = null, send_ack = true } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (source_override) fd.append('source_override', source_override);
    fd.append('send_ack', send_ack ? 'true' : 'false');
    return dealerApi.post('/dealers/marketplace-leads/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  createManual: (payload) => dealerApi.post('/dealers/marketplace-leads/manual', payload).then((r) => r.data),
  list: (params = {}) => dealerApi.get('/dealers/marketplace-leads', { params }).then((r) => r.data),
  analytics: () => dealerApi.get('/dealers/marketplace-leads/analytics').then((r) => r.data),
  update: (id, payload) => dealerApi.patch(`/dealers/marketplace-leads/${id}`, payload).then((r) => r.data),
  markSold: (id, payload) => dealerApi.post(`/dealers/marketplace-leads/${id}/mark-sold`, payload).then((r) => r.data),
  delete: (id) => dealerApi.delete(`/dealers/marketplace-leads/${id}`).then((r) => r.data),
  clearAll: () => dealerApi.post('/dealers/marketplace-leads/clear-all', null, { params: { confirm: 'DELETE' } }).then((r) => r.data),
  syncTwilioStatus: () => dealerApi.post('/dealers/marketplace-leads/sync-twilio-status').then((r) => r.data),
  whatsappHealth: () => dealerApi.get('/dealers/marketplace-leads/whatsapp-health').then((r) => r.data),
  whatsappRepairWebhooks: () => dealerApi.post('/dealers/marketplace-leads/whatsapp-repair-webhooks').then((r) => r.data),
  whatsappInboundDebug: () => dealerApi.get('/dealers/marketplace-leads/inbound-debug').then((r) => r.data),
  whatsappReplayInbound: () => dealerApi.post('/dealers/marketplace-leads/whatsapp-replay-inbound').then((r) => r.data),
};

export const dealerDashboard = {
  summary: () => dealerApi.get('/dealers/dashboard/summary').then((r) => r.data),
};

// Public (no auth)
export const dealerPublic = {
  site: (slug) => dealerApi.get(`/public/dealers/${slug}`).then((r) => r.data),
  listing: (slug, listingId) => dealerApi.get(`/public/dealers/${slug}/cars/${listingId}`).then((r) => r.data),
  track: (slug, listingId, action) =>
    dealerApi.post(`/public/dealers/${slug}/cars/${listingId}/track`, null, { params: { action } }).then((r) => r.data),
  submitLead: (slug, payload) =>
    dealerApi.post(`/public/dealers/${slug}/leads`, payload).then((r) => r.data),
};

export const buildImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/uploads/')) return `${API}${path}`;
  if (path.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${path}`;
  return path;
};
